import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { StreamingSource, StreamingSourceState } from 'fym-shared/src/music24/streaming';
import { TypedMemo } from '../../../common/types';
import { sourceLook } from '../streamingSources';
import { streamingMessages as messages } from '../streamingMessages';
import { SourceMark } from './SourceMark';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from '../Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */

const STATE_CLASS: Record<StreamingSourceState, string | undefined> = {
  ok: undefined,
  firstRead: styles.pillFirstRead,
  reconnect: styles.pillReconnect,
  failed: styles.pillFailed,
  noArtist: styles.pillNoArtist,
  disconnected: styles.pillDisconnected,
};

interface StreamingSourcePillsProps {
  sources: StreamingSource[];
  off: string[];
  onToggle: (integrationType: string) => void;
}

/**
 * One pill per connected source (design b1, f1). The dot shows the state: green = read today,
 * violet = first read, red = needs Reconnect or failed, grey = disconnected (history only) or turned
 * off by the reader. A click
 * turns the source off in the chart and in the totals that add up day by day.
 */
const StreamingSourcePillsComponent: React.FC<StreamingSourcePillsProps> = ({ sources, off, onToggle }) => {
  const intl = useIntl();
  const pills = useMemo(
    () =>
      sources.map((source) => {
        const look = sourceLook(source.integrationType);
        const isOff = off.includes(source.integrationType);
        return (
          <button
            key={source.integrationType}
            type="button"
            aria-pressed={!isOff}
            data-state={source.state}
            title={intl.formatMessage(messages.sourceTurnOff, { source: look.name })}
            className={clsx(styles.pill, STATE_CLASS[source.state], isOff && styles.pillOff)}
            onClick={() => onToggle(source.integrationType)}
          >
            <SourceMark integrationType={source.integrationType} />
            {look.shortName}
            <span className={styles.pillDot} />
          </button>
        );
      }),
    [intl, off, onToggle, sources]
  );

  return (
    <div className={styles.pills}>
      <span className={styles.pillsKey}>{intl.formatMessage(messages.sourcesLabel)}</span>
      {pills}
    </div>
  );
};

export const StreamingSourcePills = TypedMemo(StreamingSourcePillsComponent);
